import { delay, TimeoutException } from './Promise';

type SuccessOrError<T> = { error: any } | { success: T };

function isSuccess<T>(x: SuccessOrError<T>): x is { success: T } {
    return 'success' in x;
}

function isError<T>(x: SuccessOrError<T>): x is { error: any } {
    return 'error' in x;
}

export class RetryPolicy<T> {
    public constructor(
        retries: number,
        sleepDurationProvider: (count: number) => number,
        handleResult?: (result: T) => boolean,
        handle?: (error: any) => boolean
    ) {
        this.retries = retries;
        this.sleepDurationProvider = sleepDurationProvider;
        this.handleResult = handleResult;
        this.handle = handle;
    }

    public async execute(fn: () => Promise<T>, timeout?: Promise<never>): Promise<T> {
        for (let attempt = 0; ; attempt++) {
            const successOrError = await this.invoke(fn, timeout);

            const shouldRetry =
                attempt >= this.retries
                    ? false
                    : isSuccess(successOrError)
                        ? this.handleResult && this.handleResult(successOrError.success)
                        : this.handle && this.handle(successOrError.error);

            if (shouldRetry) {
                const sleepDuration = this.sleepDurationProvider(attempt);
                if (sleepDuration) {
                    await delay(sleepDuration);
                    continue;
                }
            }

            if (isError(successOrError)) throw successOrError.error;

            return successOrError.success;
        }
    }

    private async invoke(fn: () => Promise<T>, timeout?: Promise<never>): Promise<SuccessOrError<T>> {
        try {
            const promises: Promise<T>[] = [];
            promises.push(fn());
            if (timeout) promises.push(timeout);

            const result = await Promise.race(promises);
            return { success: result };
        } catch (e: any) {
            if (e instanceof TimeoutException) throw e;

            return { error: e };
        }
    }

    private readonly retries: number;
    private readonly sleepDurationProvider: (count: number) => number;
    private readonly handleResult?: (result: T) => boolean;
    private readonly handle?: (error: any) => boolean;
}
